import { getRailsUpdate } from '@/common/utils/getRailsUpdate'
import { mapToDeletion } from '@/common/utils/railsHelpers'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import {
  GovernmentAllocationEntryProductCategoryCurrencyValidationSchema,
  GovernmentAllocationEntryProductCategoryQuantityValidationSchema,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import {
  transformNewProduct,
  transformNewProductQuantity,
  transformUpdatedProduct,
  transformUpdatedProductQuantity,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/utils/transformNewProduct'
import { CompanyGovernmentAllocationEntryProductCategory } from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'

type FormProductCategory = GovernmentAllocationEntryProductCategoryCurrencyValidationSchema | GovernmentAllocationEntryProductCategoryQuantityValidationSchema

const isCurrencyForm = (form: FormProductCategory): form is GovernmentAllocationEntryProductCategoryCurrencyValidationSchema => {
  return 'total_value' in form
}

const getExistingProductId = (
  productId: string,
  existingProducts: { id: string; product: { id: string } }[],
) => {
  return existingProducts.find((p) => p.product.id === productId)?.id
}

export const transformNewProductCategory = (
  formProductCategory: FormProductCategory,
  productCategory: CompanyGovernmentAllocationEntryProductCategory,
) => {
  const existingProducts = productCategory?.entry_products || []
  const existingProductIds = existingProducts.map((p) => p.product.id)

  if (isCurrencyForm(formProductCategory)) {
    return {
      product_category_id: formProductCategory.product_category.id,
      total_value: formProductCategory.total_value ? unFormatCurrency(formProductCategory.total_value) : 0,
      entry_products_attributes: [
        ...getRailsUpdate(
          existingProducts,
          formProductCategory.entry_products || [],
          (x) => x.product.id,
          (x) => x.product.id,
          mapToDeletion,
          (product) => (product.id ? transformUpdatedProduct(product) : transformNewProduct(product)),
        ),
        ...(formProductCategory.entry_products
          ?.filter((product) => product.id || existingProductIds.includes(product.product.id))
          ?.map((product) => {
            const id = product.id || getExistingProductId(product.product.id, existingProducts)
            return transformUpdatedProduct({ ...product, id })
          }) || []),
      ],
    }
  }

  return {
    product_category_id: formProductCategory.product_category.id,
    total_quantity: formProductCategory.total_quantity ? Number(unFormatUnknownUnit(formProductCategory.total_quantity)) || 0 : 0,
    entry_products_attributes: [
      ...getRailsUpdate(
        existingProducts,
        formProductCategory.entry_products || [],
        (x) => x.product.id,
        (x) => x.product.id,
        mapToDeletion,
        (product) => (product.id ? transformUpdatedProductQuantity(product) : transformNewProductQuantity(product)),
      ),
      ...(formProductCategory.entry_products
        ?.filter((product) => product.id || existingProductIds.includes(product.product.id))
        ?.map((product) => {
          const id = product.id || getExistingProductId(product.product.id, existingProducts)
          return transformUpdatedProductQuantity({ ...product, id })
        }) || []),
    ],
  }
}

export const transformUpdatedProductCategory = (
  formProductCategory: FormProductCategory,
  productCategory: CompanyGovernmentAllocationEntryProductCategory,
) => {
  const { product_category_id, ...transformedProductCategory } = transformNewProductCategory(
    formProductCategory,
    productCategory,
  )

  return {
    id: formProductCategory.id,
    ...transformedProductCategory,
  }
}
